import React, { useState } from 'react';
import {
  Box,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Switch,
  TextField,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Mic as MicIcon,
  Pause as PauseIcon,
  MyLocation as MyLocationIcon,
  Send as SendIcon,
} from '@mui/icons-material';

export default function ChatInput({
  value,
  onChange,
  onSend,
  onKeyDown,
  onShareLocation,
  onStartVoiceRecording,
  onStopVoiceRecording,
  isLocating,
  locationLabel,
  isRecording,
  voiceError,
  disabled,
  isLoading,
  centered = false,
  placeholder = 'Ask about fire risk, incidents, situation reports, or tactics...',
  // Plain-language toggle (D2 §3.3): undefined hides it (non-Guardian scopes).
  plainLanguage,
  onTogglePlainLanguage,
}) {
  const [menuAnchor, setMenuAnchor] = useState(null);

  const handleOpenMenu = (e) => setMenuAnchor(e.currentTarget);
  const handleCloseMenu = () => setMenuAnchor(null);

  const handleShareLocation = () => {
    handleCloseMenu();
    onShareLocation?.();
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: centered ? '100%' : 768,
        mx: 'auto',
        px: centered ? 0 : 3,
        py: centered ? 0 : 2,
        flexShrink: 0,
        ...(centered
          ? {}
          : {
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.default',
            }),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '28px',
          px: 1,
          py: 0.5,
          minHeight: 52,
          boxShadow: centered ? 2 : 0,
          transition: 'box-shadow 0.2s ease',
          '&:focus-within': {
            borderColor: 'primary.main',
            boxShadow: centered ? 3 : 1,
          },
        }}
      >
        <IconButton
          size="small"
          onClick={handleOpenMenu}
          disabled={disabled}
          sx={{ color: 'text.secondary', ml: 0.5, alignSelf: 'center' }}
          aria-label="Add attachment"
        >
          <AddIcon />
        </IconButton>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleCloseMenu}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          slotProps={{
            paper: {
              sx: { borderRadius: 2, minWidth: 200, mt: -1 },
            },
          }}
        >
          {/* "Upload document" removed: the agent has no /api/upload route.
              Archived in .archive/useChatApp-fileUpload-BAK.js (2026-09-25 gap closure). */}
          <MenuItem onClick={handleShareLocation} disabled={isLocating || !onShareLocation}>
            <ListItemIcon>
              {isLocating ? (
                <CircularProgress size={18} />
              ) : (
                <MyLocationIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText
              primary="Share location"
              secondary={locationLabel || 'For near-me questions (~50 km)'}
            />
          </MenuItem>
        </Menu>

        <TextField
          fullWidth
          multiline
          maxRows={6}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          variant="standard"
          InputProps={{ disableUnderline: true }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            '& .MuiInputBase-root': {
              fontSize: '1rem',
              alignItems: 'center',
              py: 0,
              minHeight: 36,
            },
            '& .MuiInputBase-input': {
              py: '8px',
              lineHeight: '20px',
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiInputBase-input::placeholder': {
              color: 'text.secondary',
              opacity: 0.8,
            },
          }}
        />

        <IconButton
          size="small"
          onClick={isRecording ? onStopVoiceRecording : onStartVoiceRecording}
          // Stop must always work while recording (pass 9 F2).
          disabled={!isRecording && (disabled || isLoading)}
          sx={{
            color: isRecording ? 'primary.main' : 'text.secondary',
            alignSelf: 'center',
          }}
          aria-label={
            isRecording ? 'Pause recording and send' : 'Start voice recording'
          }
          title={
            isRecording
              ? 'Pause and send'
              : 'Tap to talk, then pause to send'
          }
        >
          {isRecording ? <PauseIcon fontSize="small" /> : <MicIcon fontSize="small" />}
        </IconButton>

        <IconButton
          onClick={onSend}
          disabled={!value.trim() || disabled || isLoading}
          sx={{
            bgcolor: value.trim() && !disabled ? 'primary.main' : 'action.disabledBackground',
            color: value.trim() && !disabled ? 'primary.contrastText' : 'text.disabled',
            width: 36,
            height: 36,
            alignSelf: 'center',
            mr: 0.5,
            '&:hover': {
              bgcolor: value.trim() && !disabled ? 'primary.dark' : 'action.disabledBackground',
            },
            '&.Mui-disabled': {
              bgcolor: 'action.disabledBackground',
            },
          }}
          aria-label="Send message"
        >
          <SendIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
      {voiceError && (
        <Box sx={{ px: 2, pt: 0.75, color: 'error.main', fontSize: '0.75rem' }}>
          {voiceError}
        </Box>
      )}
      {onTogglePlainLanguage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 0.5 }}>
          <Tooltip title="Answers use everyday words instead of radio codes or local terms — handy for mutual aid or working with other agencies.">
            <FormControlLabel
              sx={{ m: 0 }}
              control={(
                <Switch
                  size="small"
                  checked={Boolean(plainLanguage)}
                  onChange={(e) => onTogglePlainLanguage(e.target.checked)}
                  inputProps={{ 'aria-label': 'Plain language' }}
                />
              )}
              label="Plain language"
              componentsProps={{ typography: { variant: 'caption', color: 'text.secondary' } }}
            />
          </Tooltip>
        </Box>
      )}
    </Box>
  );
}
